import React, { useEffect, useState } from "react";
import { Mountain, Globe, User, LogOut } from "lucide-react";
import { SkiPlanEditor } from "./components/SkiPlanEditor";
import { PlanList } from "./components/PlanList";
import { SharedPlansLibrary } from "./components/SharedPlansLibrary";
import { AuthModal } from "./components/AuthModal";
import { SkiPlan } from "./types";
import * as pocketbaseService from "./utils/pocketbaseService";
import { pb, getCurrentUserModel as getPBCurrentUser } from "./utils/pocketbaseClient";
import { compressImage, shouldCompressImage } from "./utils/imageCompression";

type EditorTarget =
    | { kind: "local"; localId: string }
    | { kind: "cloud"; cloudId: string };

export default function App() {
    const [plans, setPlans] = useState<SkiPlan[]>(() => {
        const savedPlans = localStorage.getItem("skiPlans");
        if (savedPlans) {
            try {
                return JSON.parse(savedPlans);
            } catch (error) {
                console.error("Erreur lors du chargement des plans:", error);
            }
        }

        const today = new Date().toISOString().split("T")[0];
        return [
            {
                id: "1",
                name: "",
                location: "Bessans",
                date: today,
                raceType: "",
                siteImage: null,
                elements: [],
                paths: [],
            } as SkiPlan,
        ];
    });

    const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showSharedLibrary, setShowSharedLibrary] = useState(false);

    const [currentUser, setCurrentUser] = useState<any>(null);

    const [isSyncing, setIsSyncing] = useState(false);
    const [publicRefreshKey, setPublicRefreshKey] = useState(0);

    const [cloudPlan, setCloudPlan] = useState<SkiPlan | null>(null);

    useEffect(() => {
        try {
            localStorage.setItem("skiPlans", JSON.stringify(plans));
        } catch (error) {
            if (error instanceof DOMException && error.name === "QuotaExceededError") {
                console.error("localStorage quota exceeded - trying to clean up thumbnails");
                try {
                    const plansWithoutThumbnails = plans.map((p) => ({ ...p, thumbnail: undefined }));
                    localStorage.setItem("skiPlans", JSON.stringify(plansWithoutThumbnails));
                    setPlans(plansWithoutThumbnails);
                    alert("Espace de stockage insuffisant. Les aperçus visuels ont été désactivés.");
                } catch (retryError) {
                    console.error("Still cannot save even without thumbnails:", retryError);
                    if (retryError instanceof DOMException && retryError.name === "QuotaExceededError") {
                        alert("Erreur: Espace de stockage local saturé. Veuillez supprimer quelques plans.");
                    }
                }
            } else {
                console.error("Error saving to localStorage:", error);
            }
        }
    }, [plans]);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const user = getPBCurrentUser();
        if (user) {
            setCurrentUser(user);
            return;
        }
        if (pb.authStore.isValid) {
            setCurrentUser(pb.authStore.model);
        }
    };

    const handleAuthSuccess = async () => {
        setShowAuthModal(false);
        await checkAuth();
    };

    const handleLogout = async () => {
        await pocketbaseService.logout();
        setCurrentUser(null);
    };

    // ===== Optimiser les images existantes (1 fois) =====
    useEffect(() => {
        const optimizeExistingImages = async () => {
            let hasChanges = false;

            const optimizedPlans = await Promise.all(
                plans.map(async (plan) => {
                    if (plan.siteImage && plan.siteImage.startsWith("data:image/") && shouldCompressImage(plan.siteImage)) {
                        const compressed = await compressImage(plan.siteImage);
                        hasChanges = true;
                        return { ...plan, siteImage: compressed };
                    }
                    return plan;
                })
            );

            if (hasChanges) setPlans(optimizedPlans);
        };

        const timer = setTimeout(optimizeExistingImages, 1000);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const upsertLocalPlan = (next: SkiPlan) => {
        setPlans((prev) => {
            const idx = prev.findIndex((p) => p.id === next.id);
            if (idx === -1) return [...prev, next];

            const copy = [...prev];
            copy[idx] = { ...copy[idx], ...next };
            return copy;
        });
    };

    const deleteLocalPlan = (localId: string) => {
        setPlans((prev) => prev.filter((p) => p.id !== localId));
    };

    // ===== Navigation =====

    const handleCreateNew = () => {
        const today = new Date().toISOString().split("T")[0];

        const newPlan: SkiPlan = {
            id: Date.now().toString(),
            name: "",
            location: "Bessans",
            date: today,
            raceType: "",
            siteImage: null,
            elements: [],
            paths: [],
            userId: currentUser?.id,
            userName: currentUser?.name ?? "",
            isPublic: false,
            isReadOnly: false,
            cloudId: undefined,
            collaborators: [],
        };

        upsertLocalPlan(newPlan);

        setEditorTarget({ kind: "local", localId: newPlan.id });
        setIsCreating(true);
    };

    const handleEditLocal = (plan: SkiPlan) => {
        if (plan.cloudId) {
            setEditorTarget({ kind: "cloud", cloudId: plan.cloudId });
        } else {
            setEditorTarget({ kind: "local", localId: plan.id });
        }
        setIsCreating(false);
    };

    const handleCancelEditor = () => {
        setEditorTarget(null);
        setIsCreating(false);
    };

    const handleSaveLocal = async (plan: SkiPlan) => {
        upsertLocalPlan(plan);
    };

    const handleDelete = async (localId: string) => {
        const planToDelete = plans.find((p) => p.id === localId);
        if (!planToDelete) return;

        if (confirm("Êtes-vous sûr de vouloir supprimer ce plan ?")) {
            deleteLocalPlan(localId);
        }
    };

    const handleSelectSharedPlan = (planFromLibrary: any) => {
        if (!planFromLibrary?.cloudId) {
            alert("Plan cloud invalide (cloudId manquant)");
            return;
        }
        setEditorTarget({ kind: "cloud", cloudId: planFromLibrary.cloudId });
        setIsCreating(false);
    };

    // ===== Résolution plan =====
    const selectedPlan: SkiPlan | null = (() => {
        if (!editorTarget) return null;
        if (editorTarget.kind === "local") {
            return plans.find((p) => p.id === editorTarget.localId) ?? null;
        }
        return null;
    })();

    const cloudId = editorTarget?.kind === "cloud" ? editorTarget.cloudId : null;

    useEffect(() => {
        if (!cloudId) {
            setCloudPlan(null);
            return;
        }

        let alive = true;

        (async () => {
            setIsSyncing(true);
            try {
                const res = await pocketbaseService.getPlanWithImage(cloudId);

                if (!alive) return;

                if (!res.data?.plan) {
                    alert(res.error || "Impossible d'ouvrir le plan");
                    setEditorTarget(null);
                    return;
                }

                setCloudPlan(res.data.plan);
            } catch (e: any) {
                console.error("Open cloud plan failed:", e);
                if (alive) {
                    alert(e?.message || "Erreur ouverture plan");
                    setEditorTarget(null);
                }
            } finally {
                if (alive) setIsSyncing(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [cloudId]);

    const planForEditor = editorTarget?.kind === "local" ? selectedPlan : cloudPlan;

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Mountain className="w-8 h-8 text-blue-600" />
                            <div>
                                <h1>Plans de Compétition - Ski de Fond</h1>
                                <p className="text-sm text-gray-600">
                                    {currentUser ? "Connecté — vous pouvez collaborer et publier depuis l’éditeur" : "Connectez-vous pour partager vos plans"}
                                    {isSyncing ? " (sync…)" : ""}
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => setShowSharedLibrary(true)}
                                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                                title="Voir les plans partagés"
                            >
                                <Globe className="w-4 h-4" />
                                <span className="text-sm">Plans publics</span>
                            </button>

                            {currentUser ? (
                                <div className="flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-lg">
                                    <User className="w-4 h-4 text-gray-600" />
                                    <span className="text-sm">{currentUser.name || currentUser.email}</span>
                                    <button
                                        onClick={handleLogout}
                                        className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                                        title="Se déconnecter"
                                    >
                                        <LogOut className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setShowAuthModal(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    <User className="w-4 h-4" />
                                    <span className="text-sm">Se connecter</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {planForEditor ? (
                    <SkiPlanEditor
                        plan={planForEditor}
                        onSave={handleSaveLocal}
                        onCancel={handleCancelEditor}
                        isCreating={isCreating}
                        currentUserId={currentUser?.id}
                    />
                ) : (
                    <PlanList plans={plans} onCreateNew={handleCreateNew} onEdit={handleEditLocal} onDelete={handleDelete} />
                )}
            </main>

            {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} onSuccess={handleAuthSuccess} />}

            {showSharedLibrary && (
                <SharedPlansLibrary
                    onClose={() => setShowSharedLibrary(false)}
                    onSelectPlan={(p) => {
                        handleSelectSharedPlan(p);
                        setShowSharedLibrary(false);
                        setPublicRefreshKey((k) => k + 1);
                    }}
                    currentUserId={currentUser?.id}
                    refreshKey={publicRefreshKey}
                />
            )}
        </div>
    );
}